import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  mapToDropdownOptionArrayWithSep
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import CheckEmailModal from 'components/guarantors/employee/CheckEmailModal';
import UpsertEmployeeForm from 'components/guarantors/employee/UpsertEmployeeForm';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormApi, FormState } from 'final-form';
import moment from 'moment';
import React, { Component } from "react";
import CopyToClipboard from 'react-copy-to-clipboard';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Modal, Popup } from 'semantic-ui-react';
import { getAllCompanies, getCompanies, getCompanyData } from 'services/companyServices';
import { getAllActiveDivisionsByCompany } from 'services/divisionServices';
import { createMipocoAccessLink, deleteEmployee, findEmployeeById, searchEmployees } from 'services/employeeServices';
import { getGuarantorData } from 'services/guarantorServices';
import { getCompanyShippableMedicaments } from 'services/medicamentServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import { EmployeeAccessTokenType, LabInvoiceTo, Privilege } from 'ts-types/api.enums';
import {
  CompanyDto,
  DivisionDto,
  EmployeeDto,
  EmployeeSearchRequest,
  GuarantorDto,
  MedicamentDto,
  UpsertEmployeeDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { getAllMandatesWithDeleted } from 'services/mandateServices';
import createDecorator from 'final-form-calculate';

const EmployeeViewSegment = styled(PraxStyledSegment)`

  .ui.button.toggle.active,
  .ui.button.toggle.active:hover {
    background-color: #2185D0 !important;
  }

  .results-summary {
    display: inline-block;
    color: #575757;

    .highlight-value {
      color: rgb(48 145 218);
      font-weight: bold;
    }
  }
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

const MipocoAccessDiv = styled.div`
  min-width: 25rem;
  min-height: 5rem;
  padding: 0.875rem;

  .link-box {
    word-break: break-all;
    margin: 0.5rem 0;
    padding: 0.5rem;
    background-color: #f5f5f5;
  }
`;

interface ExtendedEmployeeSearchRequest extends EmployeeSearchRequest {
  guarantorCompany: boolean,
  searchCompanies: Array<CompanyDto>
  searchCompanyOptions: Array<DropdownOption>
}

interface EmployeeFieldProps extends FormState {
  values: Partial<ExtendedEmployeeSearchRequest>
}

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    GuarantorsConsumerRenderProps {
}

interface State {
  refDataLoaded: boolean,
  searchValues: Partial<ExtendedEmployeeSearchRequest>,
  foundEmployees: Array<EmployeeDto>,
  employeesLoaded: boolean,
  addEditModalOpen: boolean,
  checkEmailModalOpen: boolean,
  employee?: UpsertEmployeeDto,
  employeeId?: number,
  companies: Array<CompanyDto>,
  allCompanies: Array<CompanyDto>,
  companiesOptions: Array<DropdownOption>,
  superMandateSearchOptions: Array<DropdownOption>,
  divisions: Array<DivisionDto>,
  medicaments: Array<MedicamentDto>,
  company: CompanyDto,
  mipocoAccessView: boolean,
  mipocoUrl?: string,
  guarantor?: GuarantorDto,

  cancelTokenSource: CancelTokenSource,
}

class EmployeesView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialSearchValues: Partial<ExtendedEmployeeSearchRequest> = {
    companyId: undefined,
    searchKey: "",
    includesInactive: false
  };

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();
    const mandateId = props.currentUser!.userConfig.mandateId;

    this.initialSearchValues.companyId = props.selectedCompanyId;
    this.initialSearchValues.mandateId = mandateId;

    this.state = {
      refDataLoaded: false,
      foundEmployees: [],
      employeesLoaded: false,
      addEditModalOpen: false,
      checkEmailModalOpen: false,
      searchValues: {...this.initialSearchValues},
      companies: [],
      allCompanies: [],
      companiesOptions: [],
      superMandateSearchOptions: [],
      divisions: [],
      medicaments: [],
      mipocoAccessView: false,
      company: {
        guarantorCompany: false,
        active: true,
        city: "",
        companySubType: "",
        companyType: "",
        adminEmails: "",
        description: "",
        guarantorId: -1,
        id: -1,
        mandateId: mandateId,
        logoId: "",
        numberOfEmployees: 0,
        parentGuarantorCompanyId: -1,
        registrationState: "",
        selfOnboarding: false,
        street: "",
        testPoolingType: "",
        testRegistrationType: "",
        testType: "",
        testSampleMethod: "",
        zip: "",
        labInvoiceTo: LabInvoiceTo.GUARANTOR,
        automaticCertificateNotification: false,
        acceptCD: false,
        acceptCDRemark: "",
        testCertificate: false,
        vaccinationCertificate: false,
        recoveryCertificate: false
      },
      cancelTokenSource
    };

    this.fetchData(cancelTokenSource);

  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchData = async (cancelTokenSource: CancelTokenSource) => {

    try {
      const {currentUser} = this.props;
      const mandateId = currentUser!.userConfig.mandateId;

      const companies = await getAllCompanies(mandateId, cancelTokenSource);
      const allCompanies = await getCompanies(cancelTokenSource);

      const allMandates = await getAllMandatesWithDeleted(cancelTokenSource);
      const superMandateSearchOptions = mapToDropdownOptionArray(allMandates, "name");

      if (companies.length > 0) {

        this.setState({
          superMandateSearchOptions,
          companies,
          allCompanies
        });

        this.initialSearchValues = {
          ...this.initialSearchValues,
          searchCompanies: companies,
          searchCompanyOptions: mapToDropdownOptionArrayWithSep(companies, ["description", "city"], "id"),
          companyId: this.props.selectedCompanyId
        };

        if (!this.props.selectedCompanyId) {
          const selectedCompanyId = companies[0].id;
          this.props.setSelectedCompanyId(selectedCompanyId)
          this.initialSearchValues = {
            ...this.initialSearchValues,
            companyId: selectedCompanyId
          };
        }
      }

      this.triggerEmployeeSearch(this.initialSearchValues);

    } finally {
      this.setState({
        refDataLoaded: true
      });
    }
  }

  toggleMipocoAccessView = () => {
    this.setState(prevState => ({
      mipocoAccessView: !prevState.mipocoAccessView
    }));
  };

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findEmployeeById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  openCheckEmailModal = () => {
    this.setState(({}: State) => {
      return {
        checkEmailModalOpen: true
      }
    });
  }

  showModal = async (id?: number, employee?: UpsertEmployeeDto) => {

    const {searchValues, cancelTokenSource} = this.state;

    const companyId = searchValues.companyId!;
    let guarantorId = 0;
    if (employee) {
      guarantorId = employee.guarantorId;
    }

    const divisions = await getAllActiveDivisionsByCompany(companyId.toString(), cancelTokenSource);
    const medicaments = await getCompanyShippableMedicaments(companyId, cancelTokenSource);
    const company = await getCompanyData(companyId, cancelTokenSource);
    let guarantor = {
      id: 0,
      description: "",
      city: "",
      street: "",
      zip: "",
      bagNumber: "",
      tariffType: "",
      selection: false,
      active: true,
      eanNumber: ""
    };

    if (guarantorId) {
      guarantor = await getGuarantorData(guarantorId, cancelTokenSource);
    }

    this.setState(({}: State) => {
      return {
        divisions,
        medicaments,
        company,
        addEditModalOpen: true,
        employee: employee,
        employeeId: id,
        guarantor
      }
    });
  };

  showCheckEmailModal = () => {

    this.setState(({}: State) => ({
      checkEmailModalOpen: true
    }));
  };

  hideCheckEmailModal = () => {

    this.setState(({}: State) => ({
      checkEmailModalOpen: false
    }));
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      addEditModalOpen: false,
      employee: undefined,
      employeeId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        employeesLoaded: true
      })
    };

    searchEmployees(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundEmployees: response
          });
        })
        .catch(noop)
        .finally(onFinally);
    this.hideModal();
  };

  handleChange = async ({values}: EmployeeFieldProps, form: FormApi) => {
    const {cancelTokenSource} = this.state;
    if (!this.mounted) {
      return;
    }

    let searchValues = {...values};
    const companies = searchValues.searchCompanies ? searchValues.searchCompanies : [];

    if (searchValues.guarantorCompany) {
      const companyOptions = searchValues.searchCompanies
          ? searchValues.searchCompanies.filter(c => c.guarantorCompany)
          : [];
      const selectedCompany = companyOptions.filter(c => c.id === searchValues.companyId);
      if (selectedCompany.length === 0) {
        searchValues.companyId = undefined;
        if (companyOptions.length > 0) {
          searchValues.companyId = companyOptions[0].id;
        }
        form.change("companyId", searchValues.companyId);
        return;
      }

      values.searchCompanyOptions =
          mapToDropdownOptionArrayWithSep(companyOptions, ["description", "city"], "id")
    } else {
      values.searchCompanyOptions = mapToDropdownOptionArrayWithSep(companies, ["description", "city"], "id")
    }

    if (searchValues.companyId) {
      this.props.setSelectedCompanyId(searchValues.companyId);
      const compId = searchValues.companyId;
      // search for divisions
      const divisions = await getAllActiveDivisionsByCompany(searchValues.companyId.toString(), cancelTokenSource);
      const company = await getCompanyData(compId, cancelTokenSource);
      this.setState({
        divisions: divisions,
        company: company
      });

    }

    this.setState({
      searchValues: searchValues
    });

    this.triggerEmployeeSearch(searchValues);
  };

  triggerEmployeeSearch = debounce((searchValues: Partial<ExtendedEmployeeSearchRequest>): void => {
    if (!this.mounted) {
      return;
    }

    let values = this.state.searchValues;

    if (searchValues) {
      values = searchValues;
    }

    const {companies, cancelTokenSource} = this.state;

    if (companies && companies.length > 0) {

      this.setState({employeesLoaded: false});
      searchEmployees(values, cancelTokenSource)
          .then(response => {
                this.setState({
                  foundEmployees: response,
                  searchValues: values
                })
              }
          )
          .catch(noop)
          .finally(() => {
            this.setEmployeesLoadingFinished();
          });
    } else {
      this.setEmployeesLoadingFinished();
    }
  }, 300);

  setEmployeesLoadingFinished = (): void => {
    this.setState({
      employeesLoaded: true
    });
  }

  deleteEmployee = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteEmployee(id, cancelTokenSource).then(response => {
      this.setState(({foundEmployees}: State) => ({
        foundEmployees: foundEmployees.filter((employee: EmployeeDto) => employee.id != response.id)
      }))
    }).catch(noop);
  };

  resetCompany = createDecorator({
    field: 'mandateId',
    updates: (value, name, allValues?: Partial<ExtendedEmployeeSearchRequest>) => {
      if (allValues) {
        const { allCompanies } = this.state;

        let modifiedGuarantorCompanies = allCompanies.filter(com => com.mandateId === value);
        const companyId = modifiedGuarantorCompanies.length > 0 ? modifiedGuarantorCompanies[0].id : undefined;

        return {
          searchCompanyOptions:
              mapToDropdownOptionArrayWithSep(modifiedGuarantorCompanies, ["description", "city"], "id"),
          searchCompanies: modifiedGuarantorCompanies,
          companyId: companyId
        }
      }
      return {};
    }
  })

  render(): React.ReactNode {
    const {employeesLoaded} = this.state;
    const {getMessage} = this.props;

    return (
        <EmployeeViewSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchEmployeeSegment()}
          </div>
          <StyledViewTableRow id="employees-table">
            {!employeesLoaded
                ? <LoaderComponent message={getMessage("employee.loading")}/>
                : this.renderEmployeesSegment()}
          </StyledViewTableRow>
          {this.renderUpsertEmployeeModal()}
          {this.renderCheckEmailModal()}
        </EmployeeViewSegment>
    );
  }

  renderSearchEmployeeSegment = (): JSX.Element => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            decorators={[this.resetCompany]}
            initialValues={this.initialSearchValues}
            subscription={{values: true, pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({form, values}: FormRenderProps): React.ReactNode => {

    const {getMessage, currentUser} = this.props;
    const {companies, refDataLoaded, foundEmployees, superMandateSearchOptions} = this.state;
    const isSuperMandate = currentUser!.userConfig.modifyPatientsInOtherMandates;

    const numResults = foundEmployees.length;

    if (!refDataLoaded) {
      return <></>;
    }

    const disableAddEmployee = !(companies && companies.length > 0);

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={2}>
              {getMessage("company.title")}:
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="companyId"
                  component={Select}
                  fluid
                  options={values.searchCompanyOptions}
              />
            </Grid.Column>
            <Grid.Column width={3} verticalAlign={"middle"}>
              <Field
                  name="guarantorCompany"
                  component={CheckBox}
                  label={getMessage("employee.guarantorCompany")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={6} textAlign="right">
              <Button
                  onClick={this.showCheckEmailModal}
              >
                {getMessage("employee.checkEmail.title")}
              </Button>
              {
                  this.props.currentUserHasPrivilege(Privilege.PX_CORE_MIPICO_IMPERSONATIONS) &&
                  <Button toggle active={this.state.mipocoAccessView} onClick={this.toggleMipocoAccessView}>
                    {getMessage("employee.mipocoAccess")}
                  </Button>
              }
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={5}>
              <Field
                  name="searchKey"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          {isSuperMandate &&
              <Grid.Row>
                <Grid.Column width={2}>
                  {getMessage("patientCentral.overview.search.superMandate")}:
                </Grid.Column>
                <Grid.Column width={5}>
                  <Field
                      name="mandateId"
                      component={Select}
                      fluid
                      options={superMandateSearchOptions}
                  />
                </Grid.Column>
              </Grid.Row>
          }
          <Grid.Row>
            <Grid.Column width={2} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={5} verticalAlign={"middle"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9} textAlign="right">
              <AddButton buttonMessage={getMessage("button.add")}
                         disabled={disableAddEmployee}
                         onClick={() => this.showModal()}
              />
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </PxGrid>
    )
  };

  registrationStateCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    const tooltipTitle = getMessage(`registrationState.${cellData}`);

    if (cellData === "REJECTED") {
      return <Icon title={tooltipTitle} name="minus circle" color="red" size="large"/>
    } else if (cellData === "ACCEPTED") {
      return <Icon title={tooltipTitle} name="check circle" color="green" size="large"/>
    } else if (cellData === "PENDING") {
      return <Icon title={tooltipTitle} name="question circle" color="blue" size="large"/>
    } else if (cellData === "INITIAL") {
      return <Icon title={tooltipTitle} name="circle outline" color="grey" size="large"/>
    }
    return "";
  };

  employeeCellRenderer = ({rowData}: any) => {

    const trigger = (
        <div>{rowData.firstName} {rowData.name}</div>
    );

    const content = rowData.remark;

    if (content) {
      return (
          <div title={""}>
            <Popup
                trigger={trigger}
                popperModifiers={{
                  preventOverflow: {
                    boundary: document.getElementById("employees-table")
                  }
                }}
                content={content}
                flowing
                on="hover"
                onClose={noop}
                size="large"
                position="top center"
            />
          </div>
      );
    }

    return trigger;
  };

  birthDateCellRenderer = ({cellData}: any) => <div>{moment(cellData).format("DD.MM.YYYY")}</div>;

  addressCellRenderer = ({rowData}: any) => {
    return `${rowData.zip} ${rowData.city}`;
  };

  divisionCellRenderer = ({cellData}: any) => {
    if (cellData && cellData.length > 0) {
      return cellData;
    }
    return "";
  };

  testTypeCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData && cellData.length > 0) {
      return getMessage(`enum.InterventionExternalCode.${cellData}`);
    }
    return "";
  };

  testFrequencyCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData && cellData.length > 0) {
      return getMessage(`testFrequency.${cellData}`)
    }
    return "";
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("employee.active")
    }
    return getMessage("employee.inactive");
  };

  adminCellRenderer = ({cellData, rowData}: any) => {
    const {getMessage} = this.props;
    const {divisions} = this.state;
    const adminDivisionId = rowData.adminDivisionId;

    if (!(cellData && cellData === true)) {
      return "";
    } else if (adminDivisionId == null || !adminDivisionId) {
      return <div>{getMessage("employee.admin")}</div>;
    }

    const trigger = (
        <div>{getMessage("employee.admin")}</div>
    );

    const foundDivision = divisions.filter((division: DivisionDto) => division.id === adminDivisionId)

    if (foundDivision && foundDivision.length > 0) {
      const content = (
          <div>{getMessage("employee.adminFor")} {foundDivision[0].name}</div>
      );

      return (
          <div title={""}>
            <Popup
                trigger={trigger}
                popperModifiers={{
                  preventOverflow: {
                    boundary: document.getElementById("employees-table")
                  }
                }}
                content={content}
                flowing
                on="hover"
                onClose={noop}
                size="large"
                position="top center"
            />
          </div>
      );
    }

    return trigger;
  }

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("employee.confirmDelete")}
              onConfirm={() => this.deleteEmployee(cellData)}
          />
        </React.Fragment>
    );
  };

  mipocoActionsCellRenderer = ({rowData}: any) => {

    const {getMessage} = this.props;
    const {mipocoUrl, company} = this.state;

    const guarantorCompany = company.guarantorCompany;

    const renderMipocoAccessPopup = () => {
      const {getMessage} = this.props;
      return <MipocoAccessDiv onClick={evt => evt.stopPropagation()}>
        {
          mipocoUrl
              ?
              <div>
                <div className="info-box">{getMessage("employee.loginToMipocoPopup.info")}</div>
                <div className="link-box">{mipocoUrl}</div>
                <div className="actions-row">
                  <CopyToClipboard text={mipocoUrl}>
                    <Button type="button" compact color="grey">
                      {getMessage("actions.copyToClipboard")}
                    </Button>
                  </CopyToClipboard>
                </div>
              </div>
              :
              "Loading..."
        }
      </MipocoAccessDiv>;
    };

    return (
        <React.Fragment>
          {
              rowData.active && !guarantorCompany &&
              <ConfirmActionPopup
                  poperDependencies={mipocoUrl}
                  style={{padding: 0}}
                  renderTarget={
                    <Popup
                        trigger={<Icon name="user"/>}
                        style={PopupStyle}
                        content={getMessage("employee.actions.loginToMipocoAsEmployee")}
                        size="small"
                        position="top center"
                        inverted
                    />
                  }
                  onOpen={async () => {
                    const link = await createMipocoAccessLink(
                        rowData.id, EmployeeAccessTokenType.PERSON, this.state.cancelTokenSource);
                    this.setState({
                      mipocoUrl: link
                    });
                  }}
                  position="left center"
                  renderContent={() => renderMipocoAccessPopup()}/>
          }
          {
              rowData.companyAdmin &&
              <ConfirmActionPopup
                  poperDependencies={mipocoUrl}
                  style={{padding: 0}}
                  renderTarget={
                    <Popup
                        trigger={<Icon name="user secret"/>}
                        style={PopupStyle}
                        content={getMessage("employee.actions.loginToMipocoAsCompanyAdmin")}
                        size="small"
                        position="top center"
                        inverted
                    />
                  }
                  onOpen={async () => {
                    const link = await createMipocoAccessLink(
                        rowData.id, EmployeeAccessTokenType.COMPANY, this.state.cancelTokenSource);
                    this.setState({
                      mipocoUrl: link
                    });
                  }}
                  position="left center"
                  renderContent={() => renderMipocoAccessPopup()}/>
          }
        </React.Fragment>
    );
  };


  employeesRowGetter = ({index}: any) => {
    const {foundEmployees} = this.state;

    Object.assign(foundEmployees[index], {index: index + 1});

    return foundEmployees[index];
  };

  employeesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundEmployees} = this.state;

    const employeeId = foundEmployees[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(employeeId)}
        >
          {columns}
        </div>
    );
  };

  renderEmployeesSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundEmployees, mipocoAccessView} = this.state;

    const tableColumns = [
      {
        width: 100,
        label: "#",
        dataKey: "index"
      },
      {
        width: 150,
        label: (getMessage("employee.registrationState")),
        dataKey: "registrationState",
        cellRenderer: this.registrationStateCellRenderer
      },
      {
        width: 150,
        label: (getMessage("status")),
        dataKey: "active",
        cellRenderer: this.statusCellRenderer
      },
      {
        width: 150,
        label: (getMessage("employee.admin")),
        dataKey: "companyAdmin",
        cellRenderer: this.adminCellRenderer
      },
      {
        width: 550,
        label: (getMessage("employee.title")),
        dataKey: "firstName",
        cellRenderer: this.employeeCellRenderer
      },
      {
        width: 250,
        label: (getMessage("patient.birthDate")),
        dataKey: "birthDate",
        cellRenderer: this.birthDateCellRenderer
      },
      {
        width: 600,
        label: (getMessage("employee.companyEmail")),
        dataKey: "companyEmail"
      },
      {
        width: 400,
        label: (getMessage("employee.division")),
        dataKey: "divisionName",
        cellRenderer: this.divisionCellRenderer
      }
    ];

    if (!mipocoAccessView) {
      tableColumns.push(
          {
            width: 300,
            label: (getMessage("employee.testType")),
            dataKey: "testType",
            cellRenderer: this.testTypeCellRenderer
          },
          {
            width: 300,
            label: (getMessage("employee.testFrequency")),
            dataKey: "testFrequency",
            cellRenderer: this.testFrequencyCellRenderer
          },
          {
            width: 100,
            label: (getMessage("actions")),
            dataKey: "id",
            cellRenderer: (this.actionsCellRenderer)
          }
      );
    } else {
      tableColumns.push(
          {
            width: 200,
            label: (getMessage("employee.mipocoAccessActions")),
            dataKey: "id",
            cellRenderer: (this.mipocoActionsCellRenderer)
          }
      );
    }

    return (
        <VirtualizedTable
            rowCount={foundEmployees.length}
            rowGetter={this.employeesRowGetter}
            rowRenderer={this.employeesRowRenderer}
            columns={tableColumns}
        />

    );
  };

  renderUpsertEmployeeModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {
      addEditModalOpen,
      employee,
      employeeId,
      searchValues,
      divisions,
      company,
      medicaments,
      guarantor
    } = this.state;

    const companyId = searchValues.companyId;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("employee.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertEmployeeForm
                companyId={companyId}
                company={company}
                employee={employee}
                employeeId={employeeId}
                companyDivisions={divisions}
                companyShippable={medicaments}
                guarantor={guarantor}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderCheckEmailModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {checkEmailModalOpen} = this.state;

    return (
        <Modal
            open={checkEmailModalOpen}
            onClose={() => this.hideCheckEmailModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("employee.checkEmail.title")}</Modal.Header>
          <FormModalContentContainer>
            <CheckEmailModal
                onCancel={this.hideCheckEmailModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withAuthContext(withGuarantorsContext(EmployeesView)));
